import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  IBankData,
  IBankPaymentInstructionData,
  Bank,
  BankPaymentInstruction,
  IBankPaymentInstructionPropertyData,
  BankPaymentInstructionProperty
} from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import { provider } from '@/services';

export function useFetchBanks() {
  const queryKey = ref(['bank']);

  const { isLoading, isError, error, data: banks } = useQuery({
    queryKey: queryKey,
    queryFn: () => provider().bank.fetchBanks(),
    enabled: true,
    select: data => {
      return data.map(bank => new Bank(bank));
    }
  });

  return { queryKey, banks, error, isError, isLoading };
}

export function useFetchBank(id: MaybeRef<number>) {
  const bankIdRef = ref(id);

  const queryKey = ref(['bank', bankIdRef]);

  const { data: bank, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(bankIdRef.value)),
    queryFn: () => provider().bank.fetchBank(bankIdRef.value),
    select: data => new Bank(data)
  });

  return { bank, isLoading, error, queryKey };
}

export function useCreateBank() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-bank'],
    mutationFn: (bank: IBankData) => provider().bank.createBank(bank),

    onSuccess: data => {
      queryClient.setQueryData(['bank', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['bank']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateBank() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-bank'],
    mutationFn: (bank: IBankData) => provider().bank.updateBank(bank),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['bank', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['bank']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteBank() {
  const mutationKey = ref(['delete-bank']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) => provider().bank.deleteBank(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['bank']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

//BankPaymentInstruction
export function useFetchBankPaymentInstructions(id: MaybeRef<number | null>) {
  const bankIdRef = ref(id);
  const queryKey = ref(['payment-instruction', bankIdRef]);

  const count = ref<number>(0);

  const { isLoading, isError, error, data: paymentInstructions } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      provider().bank.fetchBankPaymentInstructions(bankIdRef.value!),
    enabled: true,
    select: data => {
      return data.map(
        paymentInstruction => new BankPaymentInstruction(paymentInstruction)
      );
    }
  });

  return { queryKey, paymentInstructions, count, error, isError, isLoading };
}

export function useFetchBankPaymentInstruction(id: MaybeRef<number>) {
  const paymentInstructionIdRef = ref(id);

  const queryKey = ref(['payment-instruction', paymentInstructionIdRef]);

  const { data: paymentInstruction, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(paymentInstructionIdRef.value)),
    queryFn: () =>
      provider().bank.fetchBankPaymentInstruction(
        paymentInstructionIdRef.value
      ),
    select: data => new BankPaymentInstruction(data)
  });

  return { paymentInstruction, isLoading, error, queryKey };
}

export function useCreateBankPaymentInstruction() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-payment-instruction'],
    mutationFn: (paymentInstruction: IBankPaymentInstructionData) =>
      provider().bank.createBankPaymentInstruction(paymentInstruction),

    onSuccess: data => {
      queryClient.setQueryData(['payment-instruction', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateBankPaymentInstruction() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-payment-instruction'],
    mutationFn: (paymentInstruction: IBankPaymentInstructionData) =>
      provider().bank.updateBankPaymentInstruction(paymentInstruction),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction', data.id])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteBankPaymentInstruction() {
  const mutationKey = ref(['delete-payment-instruction']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      provider().bank.deleteBankPaymentInstruction(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

//BankPaymentInstructionProperty
export function useFetchBankPaymentInstructionProperties(
  paymentInstructionId: MaybeRef<number>
) {
  const instructionId = ref(paymentInstructionId);
  const queryKey = ref(['payment-instruction-property', instructionId]);

  const count = ref<number>(0);

  const {
    isLoading,
    isError,
    error,
    data: paymentInstructionProperties
  } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      provider().bank.fetchBankPaymentInstructionProperties(
        instructionId.value
      ),
    enabled: computed(() => Boolean(instructionId.value)),
    select: data => {
      return data.map(
        paymentInstruction =>
          new BankPaymentInstructionProperty(paymentInstruction)
      );
    }
  });

  return {
    queryKey,
    paymentInstructionProperties,
    count,
    error,
    isError,
    isLoading
  };
}

export function useFetchBankPaymentInstructionProperty(
  instructionId: MaybeRef<number>,
  id: MaybeRef<number>
) {
  const instructionIdRef = ref(instructionId);
  const paymentInstructionIdRef = ref(id);

  const queryKey = ref([
    'payment-instruction-property',
    paymentInstructionIdRef
  ]);

  const { data: paymentInstruction, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(paymentInstructionIdRef.value)),
    queryFn: () =>
      provider().bank.fetchBankPaymentInstructionProperty(
        instructionIdRef.value,
        paymentInstructionIdRef.value
      ),
    select: data => new BankPaymentInstructionProperty(data)
  });

  return { paymentInstruction, isLoading, error, queryKey };
}

export function useCreateBankPaymentInstructionProperty(
  instructionId: MaybeRef<number>
) {
  const instructionIdRef = ref(instructionId);
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-payment-instruction-property'],
    mutationFn: (paymentInstruction: IBankPaymentInstructionPropertyData) =>
      provider().bank.createBankPaymentInstructionProperty(
        instructionIdRef.value,
        paymentInstruction
      ),

    onSuccess: data => {
      queryClient.setQueryData(['payment-instruction-property', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction-property'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateBankPaymentInstructionProperty(
  instructionId: MaybeRef<number>
) {
  const queryClient = useQueryClient();
  const instructionIdRef = ref(instructionId);

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-payment-instruction-property'],
    mutationFn: (paymentInstruction: IBankPaymentInstructionPropertyData) =>
      provider().bank.updateBankPaymentInstructionProperty(
        instructionIdRef.value,
        paymentInstruction
      ),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction-property', data.id])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction-property'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteBankPaymentInstructionProperty(
  instructionId: MaybeRef<number>
) {
  const mutationKey = ref(['delete-payment-instruction-property']);

  const queryClient = useQueryClient();
  const instructionIdRef = ref(instructionId);

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      provider().bank.deleteBankPaymentInstructionProperty(
        instructionIdRef.value,
        id
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-instruction-property'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
